import { get, omit } from "underscore";
import { entryType, equipmentStatus } from "../../constants";
import {
  GET_DIGITAL_LAB_BOOKING_INSTRUMENTS,
  LIST_BOOKING_ACCESS_CONFIGURATION_BY_EQUIPMENT,
  LIST_DIGITAL_LAB_BOOKING_INSTRUMENTS,
  REPO_GET_DIGITAL_LAB_INSTRUMENT_REPOSITORY_ENTRY,
} from "../../gql/bookingapi/queries";
import { configureAwsAppSync } from "./configureAwsAppSync";
import { API } from "aws-amplify";
import {signOut} from "../signout";
import { extractCidmAccessControlFromConfigItems } from "../../features/equipment-list/accessControlStatus";

const checkObjectInvalidity = (obj) => typeof obj !== "object" || obj === null || Array.isArray(obj);

const getOrFilter = (arrayFilterKeys, arrayFilters) => {
  return arrayFilterKeys
    ?.reduce(
      (acc, key) =>
        arrayFilters[key].length
          ? [
            ...acc,
            arrayFilters[key]?.reduce(
              (acc_i, val) => (typeof val === "string" ? [...acc_i, getGQLFilterObj({ [key]: val })] : acc_i),
              []
            ),
          ]
          : acc,
      []
    )
    .filter((arr) => arr.length);
};

export const createGQLFilterObj = (obj) => {
  if (checkObjectInvalidity(obj)) {
    return null;
  }
  const objKeys = Object.keys(obj);
  if (!objKeys.length) return null;
  const arrayFilters = objKeys.reduce(
    (acc, curr) => (Array.isArray(obj[curr]) ? { ...acc, [curr]: obj[curr] } : acc),
    {}
  );
  const arrayFilterKeys = Object.keys(arrayFilters);
  if (arrayFilterKeys.length) {
    const OrFilter = getOrFilter(arrayFilterKeys, arrayFilters);
    if (!OrFilter.length) return getGQLFilterObj(omit(obj, arrayFilterKeys));
    const simpleFilter = getGQLFilterObj(omit(obj, arrayFilterKeys));
    if (!simpleFilter)
      return {
        and: [...OrFilter?.map((arr) => ({ or: arr }))],
      };
    return {
      and: [...OrFilter?.map((arr) => ({ or: arr })), simpleFilter],
    };
  }
  return getGQLFilterObj(obj);
};

export const getGQLFilterObj = (obj) => {
  if (typeof obj !== "object" || obj === null || Array.isArray(obj)) return null;
  const result = Object.keys(obj).reduce((acc, cur) => {
    if (obj?.[cur] === null || obj?.[cur] === undefined) return acc;
    if (typeof obj?.[cur] === "string" && (obj?.[cur]?.trim() === "" || obj?.[cur]?.trim() === "all")) return acc;
    return { ...acc, [cur]: { eq: obj[cur] } };
  }, {});
  return Object.keys(result).length === 0 ? null : result;
};
const checkShouldKeepFetching = (drillData, items, limit, customizedItems, maxItems, emptyPagesInRow) => {
  if (maxItems && items.length >= maxItems) {
    return false;
  }
  if (emptyPagesInRow >= 3) {
    return false;
  }
  if (customizedItems) {
    return items.length <= customizedItems;
  }
  return drillData ? drillData : items.length < limit;
};

const returnNextToken = (_nextToken) => {
  return typeof _nextToken === "string" && _nextToken ? _nextToken : null;
};

const get_NextToken = (result, dataPath) => {
  return get(result, [...dataPath, "nextToken"]);
};

const getItems = (page, items) => {
  return Array.isArray(page) ? [...items, ...page] : items;
};

const getPage = (result, dataPath) => {
  return get(result, [...dataPath, "items"]) ?? [];
};


const __getAllDataCache = new Map();
const __getAllDataInflight = new Map();
const PERSIST_TTL_MS = 60 * 60 * 1000;
const PERSIST_PREFIX = "gad_";

const readPersistedCache = (key) => {
  try {
    const raw = localStorage.getItem(PERSIST_PREFIX + key);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.t > PERSIST_TTL_MS) {
      localStorage.removeItem(PERSIST_PREFIX + key);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const writePersistedCache = (key, value) => {
  try {
    localStorage.setItem(PERSIST_PREFIX + key, JSON.stringify({ t: Date.now(), value }));
  } catch {
    // localStorage full or unavailable
  }
};

const queryToString = (doc) => {
  if (doc?.loc?.source?.body) {
    return doc.loc.source.body;
  }
  const name = doc?.definitions?.[0]?.name?.value;
  if (name) {
    return name;
  }
  return String(doc);
}
const buildCacheKey = (args) => {
  const { query, variables, dataPath, drillData, customizedItems, awsAppSyncConfig, limit } = args;
  const { nextToken, ...rest } = variables || {};
  const base = {
    q: queryToString(query),
    v: rest,
    p: dataPath,
    d: !!drillData,
    c: !!customizedItems,
    a: !!awsAppSyncConfig,
    l: limit,
  };
  return JSON.stringify(base);
}
export const clearGetAllDataCache = () => {
  __getAllDataCache.clear();
  try {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(PERSIST_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
  } catch (e) {
    console.warn("[cache] localStorage unavailable:", e);
  }
}
export const clearGetAllDataCacheBy = (predicate) => {
  for (const key of __getAllDataCache.keys()) {
    if (predicate(key)) {
      __getAllDataCache.delete(key);
    }
  }
  try {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(PERSIST_PREFIX) && predicate(key.slice(PERSIST_PREFIX.length))) {
        localStorage.removeItem(key);
      }
    });
  } catch (e) {
    console.warn("[cache] localStorage unavailable:", e);
  }
}
const isUnauthorizedError = (e) => {
  const status =
    e?.networkError?.statusCode ??
    e?.status ??
    e?.response?.status ??
    e?.originalError?.status;

  const code =
    e?.graphQLErrors?.[0]?.extensions?.code ||
    e?.errors?.[0]?.errorType ||
    e?.code ||
    e?.name;

  return (
    status === 401 ||
    status === 403 ||
    code === "UNAUTHENTICATED" ||
    code === "UnauthorizedException" ||
    code === "NotAuthorizedException"
  );
};

/**
 * Call graphql query to fetch all pages/nextToken until we have the number of rows specified in variables.limit
 * Optional: custom/homemade caching if cache and cachekey are specified
 */
export const getAllData = async ({
 client,
 query,
 fetchPolicy = "network-only",
 variables = {},
 dataPath,
 drillData = false,
 customizedItems = false, // Loop the query for a customized item
 awsAppSyncConfig = null,
 cache = false,
 cacheTTLSeconds = 300,
 isTvMode = false,
 maxItems = null,
 onPageLoaded = null,
}) => {
  const cacheTTL = cacheTTLSeconds * 1000
  const emptyResponse = {
    items: [],
    nextToken: null,
    error: null,
  };
  if (!client || !query || !variables) return emptyResponse;
  if (!Array.isArray(dataPath) || dataPath.length === 0) return emptyResponse;
  const cacheKey = cache
    ? buildCacheKey({
      query,
      variables,
      dataPath,
      drillData,
      customizedItems,
      awsAppSyncConfig,
      limit: variables?.limit,
    })
    : null;

  if (cache && cacheKey) {
    const hit = __getAllDataCache.get(cacheKey);
    if (hit && Date.now() - hit.t <= cacheTTL) {
      return { ...hit.value, error: null };
    }
    const persisted = readPersistedCache(cacheKey);
    if (persisted && Date.now() - persisted.t <= cacheTTL) {
      __getAllDataCache.set(cacheKey, { t: persisted.t, value: persisted.value });
      return { ...persisted.value, error: null };
    }
    const inflight = __getAllDataInflight.get(cacheKey);
    if (inflight) {
      return inflight;
    }
  }

  const fetchPromise = (async () => {
  try {
    let items = [];
    let nextToken = variables?.nextToken ?? null;
    let shouldKeepFetching = true;
    let emptyPagesInRow = 0;
    const { limit = 10 } = variables;
    do {
      let result = [];
      if (awsAppSyncConfig) {
        configureAwsAppSync(awsAppSyncConfig);
        result = await API.graphql({
          client,
          query,
          variables: {
            ...variables,
            nextToken,
          },
        });
      } else {
        result = await client.query({
          query,
          fetchPolicy,
          variables: {
            ...variables,
            nextToken,
          },
        });
      }
      

      if (result?.errors && Array.isArray(result.errors) && result.errors.length > 0) {
        console.error("GraphQL errors in response:", result.errors);
        return { ...emptyResponse, error: new Error("GraphQL validation error") };
      }
      
      const page = getPage(result, dataPath);
      if (page.length === 0) {
        emptyPagesInRow++;
      } else {
        emptyPagesInRow = 0;
      }
      
      items = getItems(page, items);
      
      if (onPageLoaded) {
        onPageLoaded(items.length, [...items]);
      }
      
      const _nextToken = get_NextToken(result, dataPath);
      nextToken = returnNextToken(_nextToken);
      shouldKeepFetching = checkShouldKeepFetching(drillData, items, limit, customizedItems, maxItems, emptyPagesInRow);
      if (!shouldKeepFetching && nextToken && emptyPagesInRow >= 3) {
        console.warn(`[Perf] getAllData stopped early after ${emptyPagesInRow} empty pages (${items.length} items collected)`);
      }
    } while (nextToken !== null && shouldKeepFetching);
    
    const value = { items, nextToken };

    if (cache && cacheKey) {
      __getAllDataCache.set(cacheKey, { t: Date.now(), value });
      writePersistedCache(cacheKey, value);
    }
    
    return { ...value, error: null };
  } catch (err) {
    console.error(err.message);
    if (isTvMode && isUnauthorizedError(err)) {
      await signOut(client, null, true);
    }
    return { ...emptyResponse, error: err };
  } finally {
    if (cache && cacheKey) {
      __getAllDataInflight.delete(cacheKey);
    }
  }
  })();

  if (cache && cacheKey) {
    __getAllDataInflight.set(cacheKey, fetchPromise);
  }

  return fetchPromise;
};

export const getAllBookingInstrumentsListByUserSite = async ({ client, user }) => {
  try {
    const { items } = await getAllData({
      client,
      query: LIST_DIGITAL_LAB_BOOKING_INSTRUMENTS,
      drillData: true,
      fetchPolicy: "no-cache",
      variables: {
        limit: 1000,
        nextToken: null,
        siteName: user.site,
        filter: {
          and: [
            {
              or: [
                { entryType: { eq: entryType?.cluster } },
                { entryType: { eq: entryType?.standaloneEquipment } },
                { entryType: { eq: entryType?.clusterSubequipment } },
              ],
            },
          ],
        },
      },
      dataPath: ["data", "listDigitalLabBookingInstruments"],
    });
    return items;
  } catch (err) {
    console.error(err.message);
    return { error: err };
  }
};

export const getBookableInstrumentsListByUserSite = async ({ client, user }) => {
  try {
    const { items } = await getAllData({
      client,
      query: LIST_DIGITAL_LAB_BOOKING_INSTRUMENTS,
      drillData: true,
      fetchPolicy: "no-cache",
      variables: {
        limit: 1000,
        nextToken: null,
        siteName: user.site,
        filter: {
          and: [
            { isBookable: { eq: true } },
            {
              or: [
                { entryType: { eq: entryType?.cluster } },
                { entryType: { eq: entryType?.standaloneEquipment } },
                { entryType: { eq: entryType?.clusterSubequipment } },
              ],
            },
            { status: { eq: equipmentStatus?.active?.key } },
          ],
        },
      },
      dataPath: ["data", "listDigitalLabBookingInstruments"],
    });
    return items;
  } catch (err) {
    console.error(err.message);
    return { error: err };
  }
};
export const getFilteredInstrumentList = async ({ client, user, variables }) => {
  try {
    const { items } = await getAllData({
      client,
      query: LIST_DIGITAL_LAB_BOOKING_INSTRUMENTS,
      drillData: true,
      fetchPolicy: "no-cache",
      variables,
      dataPath: ["data", "listDigitalLabBookingInstruments"],
    });
    return items;
  } catch (err) {
    console.error(err.message);
    return { error: err };
  }
};

export const getLimitedFilteredInstrumentList = async ({ client, user, filters = {}, limit = 1000 }) => {
  try {
    // Build OR filters from the filters object with contains
    const orFilters = Object.entries(filters)
      .filter(([key, value]) => value && value.trim() !== "")
      .map(([key, value]) => ({
        [key]: { contains: value }
      }));

    const baseFilter = {
      and: [
        { isBookable: { eq: true } },
        {
          or: [
            { entryType: { eq: entryType?.cluster } },
            { entryType: { eq: entryType?.standaloneEquipment } },
            { entryType: { eq: entryType?.clusterSubequipment } },
          ],
        },
        { status: { eq: equipmentStatus?.active?.key } },
        ...(orFilters.length > 0 ? [{ or: orFilters }] : []),
      ],
    };

    // Use getAllData with customizedItems to limit results
    const { items } = await getAllData({
      client,
      query: LIST_DIGITAL_LAB_BOOKING_INSTRUMENTS,
      fetchPolicy: "no-cache",
      variables: {
        siteName: user.site,
        inventoryId: null, // Explicitly null to avoid query operation
        limit,
        filter: baseFilter,
        nextToken: null,
      },
      dataPath: ["data", "listDigitalLabBookingInstruments"],
      customizedItems: limit,
    });

    return items;
  } catch (err) {
    console.error(err.message);
    return { error: err };
  }
};

/**
 * getContainerHeight - Get the dynamic value of containter height
 * @param {string} currentEnv - If env banner is open
 * @param {number} remainingHeight - height of remaining elements in page
 * @return {any} container height : windowSize - remainingHeight
 */
export const getContainerHeight = (currentEnv, remainingHeight) => {
  return currentEnv
    ? window.innerHeight - (126 + remainingHeight) // envBanner + header + footer + pageHeader
    : window.innerHeight - (75 + remainingHeight); //  header + footer + pageHeader
};

export const getContainerHeightPercentage = (currentEnv) => {
  const containerHeight = ((window.innerHeight - (currentEnv ? 80 : 40)) / window.innerHeight) * 100;
  return containerHeight;
};

export const getClusterDetailsById = async ({ client, clusterId = "e8559bd4-ae43-4155-9d45-4104ec523243" }) => {
  const REPO_config = {
    aws_appsync_graphqlEndpoint: process.env.REACT_APP_REPO_AWS_GRAPHQL_ENDPOINT,
  };

  configureAwsAppSync(REPO_config);
  try {
    const subEquipment = await API.graphql({
      query: REPO_GET_DIGITAL_LAB_INSTRUMENT_REPOSITORY_ENTRY,
      client,
      variables: {
        id: clusterId,
      },
    });

    return subEquipment.data.getDigitalLabInstrumentRepositoryEntry.subEquipment.items;
  } catch (err) {
    console.error(err.message);
    return { error: err };
  }
};

export const getEquipment = async ({ inventoryId, client }) => {
  try {
    const result = await client.query({
      query: GET_DIGITAL_LAB_BOOKING_INSTRUMENTS,
      fetchPolicy: "no-cache",
      variables: {
        inventoryId: inventoryId,
      },
    });
    if (result) {
      const equipment = result?.data?.getDigitalLabBookingInstrument;
      if (!equipment) {
        return equipment;
      }

      try {
        const accessConfigResult = await client.query({
          query: LIST_BOOKING_ACCESS_CONFIGURATION_BY_EQUIPMENT,
          fetchPolicy: "no-cache",
          variables: {
            limit: 20,
            modelName: "BOOKINGACCESSCONFIG",
            inventoryId: { eq: inventoryId },
          },
        });

        const accessConfigItems =
          accessConfigResult?.data?.listBookingAccessConfigurationByEquipment
            ?.items || [];
        // Shared with the timeline view so both resolve CIDM identity identically.
        // CIDM name comes only from cidmGroup.groupName (never the booking group).
        const cidmGroupData = extractCidmAccessControlFromConfigItems(accessConfigItems);

        if (!cidmGroupData.cidmGroupName) {
          return equipment;
        }

        return {
          ...equipment,
          cidmGroupName: equipment.cidmGroupName ?? cidmGroupData.cidmGroupName,
          cidmGroupCidmUrl:
            equipment.cidmGroupCidmUrl ?? cidmGroupData.cidmGroupCidmUrl,
          cidmGroupMoreInfoUrl:
            equipment.cidmGroupMoreInfoUrl ?? cidmGroupData.cidmGroupMoreInfoUrl,
          cidm_group_name: equipment.cidm_group_name ?? cidmGroupData.cidmGroupName,
          cidm_group_cidm_url:
            equipment.cidm_group_cidm_url ?? cidmGroupData.cidmGroupCidmUrl,
          cidm_group_more_info_url:
            equipment.cidm_group_more_info_url ?? cidmGroupData.cidmGroupMoreInfoUrl,
        };
      } catch (accessConfigError) {
        console.error("booking access config fetch error", accessConfigError);
        return equipment;
      }
    }
  } catch (err) {
    console.log("booking data check error", err);
  }
};
